import React from "react";
import { useAuth } from "@/context/AuthContext";
import { boutiqueOf, rainbowMeta } from "@/lib/api";
import "./presence-vessel.css";

/**
 * PresenceVessel — SONMI's universal presence surface.
 *
 * This component is companion-agnostic. Every companion inhabits *this*
 * vessel — none provides its own. The vessel:
 *
 *   • is completely transparent
 *   • carries no branding, no logo, no motif
 *   • ambient-tints from the active boutique (a workspace signal, not a companion signal)
 *   • routes presence state (present · supporting · quiet · dormant) through data attrs
 *
 * Occupants are supplied as children. When `presence === "dormant"` the
 * occupant slot is hidden by CSS — the vessel simply exists.
 *
 * Props:
 *   size       — pixel width (vessel is aspect-ratio locked)
 *   presence   — "present" | "supporting" | "quiet" | "dormant"
 *   ambient    — override boutique tint. Falls back to authenticated user's boutique.
 *   children   — the occupant. Omit for an empty vessel.
 */
export default function PresenceVessel({
  size = 56,
  presence = "present",
  ambient,
  children,
}) {
  const { user } = useAuth() || {};
  const resolvedAmbient = ambient || ambientFromUser(user);
  // Resolve the boutique's registry entry so `data-boutique` / `data-tint`
  // reflect the real code even when the vessel is used in isolation (tests,
  // lab). The presence vessel itself is companion-agnostic — these
  // attributes describe the workspace, not the occupant.
  const boutiqueCode = user?.boutique_code || (ambient ? ambient.toUpperCase() : null);
  const meta = boutiqueCode ? boutiqueOf(boutiqueCode) : rainbowMeta;

  return (
    <span
      className="pv-vessel"
      data-testid="presence-vessel"
      data-ambient={resolvedAmbient}
      data-tint={resolvedAmbient}
      data-boutique={meta.code}
      data-presence={presence}
      style={{ "--pv-size": `${size}px` }}
    >
      <span className="pv-vessel__volume" aria-hidden="true" />
      <span className="pv-vessel__interior" aria-hidden="true" />
      {children ? (
        <span className="pv-vessel__occupant" data-testid="presence-occupant">
          {children}
        </span>
      ) : null}
      <span className="pv-vessel__front" aria-hidden="true" />
    </span>
  );
}

// Ambient key derives directly from the boutique registry. New boutiques
// picked up here without touching PresenceVessel — the registry drives the
// vessel just like it drives every selector and filter.
function ambientFromUser(user) {
  const code = user?.boutique_code;
  if (!code) return "rainbow";
  const meta = boutiqueOf(code);
  return meta.presenceTint || "rainbow";
}
